import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, MapPin, Clock, Shield, Phone, Mail } from "lucide-react";
import { IDS } from "@/lib/testIds";
import { useLang } from "@/lib/LangContext";

export default function Footer() {
  const { ui, services: SERVICES, cities: CITIES, business: BUSINESS, lang } = useLang();
  return (
    <footer
      data-testid={IDS.footer.root}
      className="bg-[hsl(0_0%_5%)] border-t border-border"
    >
      {/* Massive brand mark */}
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 pt-24 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="font-mono text-[10px] tracking-[0.25em] text-muted-foreground uppercase mb-6">
              {ui.footer.sitemap}
            </div>
            <h3 className="font-serif text-4xl md:text-6xl leading-[0.95] tracking-tighter mb-8">
              {ui.footer.protect} <i className="text-primary">{lang === "fr" ? "le Nouveau-Brunswick" : "New Brunswick"}</i>
              <br />
              {ui.footer.protectSub}
            </h3>
            <Link
              to="/contact#quote"
              className="inline-flex items-center gap-2 border border-foreground px-6 py-4 font-mono text-xs tracking-[0.2em] uppercase hover:bg-primary hover:border-primary transition-colors"
              data-testid="footer-cta-quote"
            >
              {ui.common.requestQuote} <ArrowUpRight size={14} />
            </Link>
            <div className="mt-8 flex flex-wrap gap-4 font-mono text-[10px] tracking-[0.25em] uppercase">
              <Link to="/insights" data-testid="footer-insights" className="text-muted-foreground hover:text-primary transition-colors">
                {lang === "fr" ? "Perspectives" : "Insights"} →
              </Link>
              <Link to="/case-studies" data-testid="footer-case-studies" className="text-muted-foreground hover:text-primary transition-colors">
                {lang === "fr" ? "Études de cas" : "Case Studies"} →
              </Link>
              <Link to="/sectors/mining" data-testid="footer-mining-sector" className="text-muted-foreground hover:text-primary transition-colors">
                {lang === "fr" ? "Secteur minier" : "Mining Sector"} →
              </Link>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="font-mono text-[10px] tracking-[0.25em] text-muted-foreground uppercase mb-6">
              {ui.footer.services}
            </div>
            <ul
              data-testid={IDS.footer.servicesList}
              className="space-y-2 font-serif text-lg"
            >
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="hover:text-primary transition-colors"
                    data-testid={`footer-service-${s.slug}`}
                  >
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-5">
            <div className="font-mono text-[10px] tracking-[0.25em] text-muted-foreground uppercase mb-6">
              {ui.footer.coverage} ({CITIES.length})
            </div>
            <ul
              data-testid={IDS.footer.citiesList}
              className="grid grid-cols-2 md:grid-cols-3 gap-y-2 gap-x-4 font-serif text-base md:text-lg"
            >
              {CITIES.map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/cities/${c.slug}`}
                    className="hover:text-primary transition-colors"
                    data-testid={`footer-city-${c.slug}`}
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="hidden md:hidden"></div>
        </div>
      </div>

      {/* Contact strip */}
      <div className="border-t border-border">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 font-mono text-xs tracking-widest">
          <a
            href={BUSINESS.phoneHref}
            data-testid="footer-phone"
            className="flex items-center gap-3 hover:text-primary transition-colors"
          >
            <Phone size={14} className="text-primary" /> {BUSINESS.phone}
          </a>
          <a
            href={BUSINESS.emailHref}
            data-testid="footer-email"
            className="flex items-center gap-3 hover:text-primary transition-colors"
          >
            <Mail size={14} className="text-primary" /> {BUSINESS.email}
          </a>
          <div className="flex items-center gap-3 text-muted-foreground">
            <Clock size={14} /> {ui.footer.dispatch}
          </div>
          <div className="flex items-center gap-3 text-muted-foreground">
            <MapPin size={14} /> {BUSINESS.address}
          </div>
        </div>
      </div>

      {/* Big word-mark */}
      <div className="border-t border-border overflow-hidden">
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.2, ease: [0.76, 0, 0.24, 1] }}
          className="mx-auto max-w-[1600px] px-6 md:px-12 py-6 md:py-10"
        >
          <div
            className="font-serif tracking-tighter leading-[0.85] text-stroke"
            style={{ fontSize: "clamp(2.5rem, 12vw, 12rem)" }}
          >
            NB · SECURITY
          </div>
        </motion.div>
      </div>

      <div className="border-t border-border">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 font-mono text-[10px] tracking-[0.25em] text-muted-foreground uppercase">
          <div>© {new Date().getFullYear()} {BUSINESS.name}. {ui.footer.rights}</div>
          <div>{BUSINESS.license}</div>
        </div>
      </div>
    </footer>
  );
}
